import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, FlameIcon, ClockIcon, TrophyIcon, UserPlusIcon, CheckCircle2Icon, SearchIcon, ArrowRightIcon, SlidersHorizontalIcon, ChevronDownIcon, DownloadIcon, CalendarIcon } from 'lucide-react';
import { LeadDetailModal, Lead } from './LeadDetailModal';
import { PremiumSelect } from './PremiumSelect';
const SORTS = ['Newest', 'Highest Intent', 'Closest To Purchase', 'Budget High to Low', 'Last Interaction'] as const;
const FILTER_DIMENSIONS = ['Intent Score', 'Budget Range', 'Timeline', 'Location', 'Matched Listing', 'Source', 'Assigned User', 'Date Range'];
const INITIAL_LEADS: Lead[] = [{
  name: 'Ahmed Al Mansoori',
  intentScore: 94,
  lookingFor: 'Toyota Prado',
  budget: 'AED 90,000',
  timeline: 'Within 7 Days',
  location: 'Dubai',
  matchedItem: 'Toyota Prado GXR 2018',
  status: 'Qualified',
  summary: 'Customer strongly prefers GCC vehicles. Not interested in imported vehicles. Ready to purchase this week.',
  phone: '+971 50 123 4567',
  email: 'ahmed@email.com',
  preferredContact: 'WhatsApp',
  preferredLanguage: 'Arabic',
  source: 'Talab AI',
  lastActive: '12 minutes ago',
  receivedDate: 'Jun 6, 2026',
  owner: 'Sara',
  nextAction: 'Call customer',
  nextActionDue: 'Today',
  consent: 'shared'
}, {
  name: 'Sara Khalifa',
  intentScore: 82,
  lookingFor: 'Nissan Patrol',
  budget: 'AED 120,000',
  timeline: 'Within 2 Weeks',
  location: 'Abu Dhabi',
  matchedItem: 'Nissan Patrol 2020',
  status: 'Negotiating',
  summary: 'Comparing 3 SUVs. Asked about financing and warranty. Strong engagement on delivery options.',
  phone: '+971 55 987 6543',
  email: 'sara.k@email.com',
  preferredContact: 'Phone Call',
  preferredLanguage: 'English',
  source: 'Talab AI',
  lastActive: '1 hour ago',
  receivedDate: 'Jun 6, 2026',
  owner: 'Fadi',
  nextAction: 'Send quotation',
  nextActionDue: 'Tomorrow',
  consent: 'shared'
}, {
  name: 'Omar Haddad',
  intentScore: 67,
  lookingFor: 'Family SUV',
  budget: 'AED 70,000 - 85,000',
  timeline: 'This Month',
  location: 'Sharjah',
  matchedItem: 'Toyota Prado GXR 2018',
  status: 'Interested',
  summary: 'Exploring options for a family vehicle. Requested availability confirmation. Budget still flexible.',
  source: 'Talab AI',
  lastActive: '3 hours ago',
  receivedDate: 'Jun 5, 2026',
  owner: 'Unassigned',
  nextAction: 'Follow up tomorrow',
  nextActionDue: 'Tomorrow',
  consent: 'requested'
}, {
  name: 'Anonymous Buyer',
  intentScore: 41,
  lookingFor: 'Compact car',
  budget: 'AED 45,000',
  timeline: 'Undecided',
  location: 'Dubai',
  matchedItem: 'Toyota Yaris 2022',
  status: 'Discovered',
  summary: 'Early-stage research. Asked a single pricing question. No timeline committed yet.',
  source: 'Talab AI',
  lastActive: '2 days ago',
  receivedDate: 'Jun 4, 2026',
  owner: 'Unassigned',
  nextAction: 'Follow up tomorrow',
  consent: 'not_shared'
}];
const PIPELINE = [{
  stage: 'Discovered',
  count: 14,
  color: 'bg-slate-400'
}, {
  stage: 'Qualified',
  count: 9,
  color: 'bg-primary-indigo'
}, {
  stage: 'Interested',
  count: 7,
  color: 'bg-ai-purple'
}, {
  stage: 'Negotiating',
  count: 4,
  color: 'bg-electric-cyan'
}, {
  stage: 'Ready To Buy',
  count: 3,
  color: 'bg-amber-500'
}, {
  stage: 'Won',
  count: 6,
  color: 'bg-green-500'
}, {
  stage: 'Lost',
  count: 2,
  color: 'bg-slate-300'
}] as const;
const STAGE_COUNTS: Record<string, number> = {
  All: 41,
  Discovered: 14,
  Qualified: 9,
  Interested: 7,
  Negotiating: 4,
  'Ready To Buy': 3,
  Won: 6,
  Lost: 2
};
function intentBucket(score: number) {
  if (score >= 90) return {
    label: 'Ready To Buy',
    color: 'text-green-700',
    bg: 'bg-green-100',
    bar: 'bg-green-500'
  };
  if (score >= 75) return {
    label: 'High Intent',
    color: 'text-primary-indigo',
    bg: 'bg-indigo-100',
    bar: 'bg-primary-indigo'
  };
  if (score >= 50) return {
    label: 'Interested',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    bar: 'bg-amber-500'
  };
  return {
    label: 'Early Stage',
    color: 'text-slate-500',
    bg: 'bg-slate-100',
    bar: 'bg-slate-400'
  };
}
export function BusinessLeads() {
  const [leads, setLeads] = useState<Lead[]>(INITIAL_LEADS);
  const [openLeadIdx, setOpenLeadIdx] = useState<number | null>(null);
  const [stageFilter, setStageFilter] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<string>('Newest');
  const [showFilters, setShowFilters] = useState(false);
  const [showStages, setShowStages] = useState(false);
  const updateLead = (idx: number, patch: Partial<Lead>) => setLeads(prev => prev.map((l, i) => i === idx ? {
    ...l,
    ...patch
  } : l));
  const filtered = useMemo(() => {
    let list = leads.map((l, idx) => ({
      l,
      idx
    }));
    if (stageFilter !== 'All') list = list.filter(({
      l
    }) => l.status === stageFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(({
        l
      }) => `${l.name} ${l.lookingFor} ${l.matchedItem} ${l.location}`.toLowerCase().includes(q));
    }
    if (sort === 'Highest Intent' || sort === 'Closest To Purchase') list = [...list].sort((a, b) => b.l.intentScore - a.l.intentScore);
    return list;
  }, [leads, stageFilter, search, sort]);
  return <div className="w-full max-w-6xl mx-auto pb-24" data-id="element-627" data-mp="3mqirn2a4chc7">
      {/* Header with inline metrics */}
      <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6" data-id="element-628" data-mp="3hxzc3zrvfc4r">
        <div data-id="element-629" data-mp="1g2jfuah3z87k">
          <p className="text-sm text-slate-500" data-id="element-630" data-mp="1g2rxefo6f1p3">
            Qualified customer opportunities identified by Talab AI.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-id="element-631" data-mp="16bos11o7s0k0">
          <InlineMetric icon={<UserPlusIcon className="w-3.5 h-3.5" data-id="element-633" data-mp="1barx2m4726rz" />} label="New" value="12" data-id="element-632" data-mp="16bf4dkqn65hh" />
          <InlineMetric icon={<CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-635" data-mp="wcpvghgq3vdd" />} label="Qualified" value="9" data-id="element-634" data-mp="wczm879le6k4" />
          <InlineMetric icon={<FlameIcon className="w-3.5 h-3.5" data-id="element-637" data-mp="nct242je28bz" />} label="Hot" value="3" data-id="element-636" data-mp="11dvokgeapknf" />
          <InlineMetric icon={<ClockIcon className="w-3.5 h-3.5" data-id="element-639" data-mp="scxgnn0qkszk" />} label="Follow-up" value="5" data-id="element-638" data-mp="sbmetsqupvb3" />
          <InlineMetric icon={<TrophyIcon className="w-3.5 h-3.5" data-id="element-641" data-mp="2hoznfu5zy5i0" />} label="Won" value="6" data-id="element-640" data-mp="2hs5a72r5kvak" />
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-642" data-mp="2wtc8gq3wtk2y">
        {/* Unified controls bar - sticky */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-200 -mx-6 px-6 py-4 mb-6" data-id="element-643" data-mp="35mfeidc86unk">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center" data-id="element-644" data-mp="30unr2bod951n">
            {/* Stage filter dropdown */}
            <div className="relative" data-id="element-645" data-mp="3fiikjua77dfs">
              <button onClick={() => setShowStages(s => !s)} className="w-full lg:w-auto flex items-center justify-between gap-3 px-3.5 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-646" data-mp="3alijxyw4b2w2">
                <span className="flex items-center gap-2" data-id="element-647" data-mp="1u6h528d4y6lt">
                  <span className={`w-1.5 h-1.5 rounded-full ${stageFilter === 'All' ? 'bg-slate-400' : PIPELINE.find(p => p.stage === stageFilter)?.color ?? 'bg-slate-400'}`} data-id="element-648" data-mp="1u93bg4oaig2g" />
                  <span className="text-slate-400 font-normal" data-id="element-649" data-mp="2jp6n5hu5tjha">Stage:</span>
                  {stageFilter}
                </span>
                <span className="flex items-center gap-2" data-id="element-650" data-mp="2oh7viso7iq21">
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500" data-id="element-651" data-mp="2shqpas9nu66v">
                    {STAGE_COUNTS[stageFilter] ?? 0}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${showStages ? 'rotate-180' : ''}`} data-id="element-652" data-mp="1vtkf7ptyyll7" />
                </span>
              </button>
              {showStages && <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowStages(false)} data-id="element-653" data-mp="29sbn2ui8nb0w" />
                  <div className="absolute left-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-large z-20 p-1.5" data-id="element-654" data-mp="3g9p61rrvotre">
                    <StageOption label="All Stages" count={STAGE_COUNTS['All']} color="bg-slate-400" active={stageFilter === 'All'} onClick={() => {
                  setStageFilter('All');
                  setShowStages(false);
                }} data-id="element-655" data-mp="3vam5r35iy32h" />
                    <div className="my-1 border-t border-slate-100" data-id="element-656" data-mp="3cke9n508scvs" />
                    {PIPELINE.map(p => <StageOption key={p.stage} label={p.stage} count={p.count} color={p.color} active={stageFilter === p.stage} onClick={() => {
                  setStageFilter(p.stage);
                  setShowStages(false);
                }} data-id="element-657" data-mp="37jqgodsgdlyx" />)}
                  </div>
                </>}
            </div>

            {/* Search + actions */}
            <div className="flex gap-2 lg:ml-auto" data-id="element-658" data-mp="15tu4d8sulghh">
              <div className="relative flex-1 lg:w-64" data-id="element-659" data-mp="15tap5zul1675">
                <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-660" data-mp="1jtlgf3zt0msu" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-661" data-mp="1etz3kvnw18kk" />
              </div>
              <div className="relative" data-id="element-662" data-mp="w3r20hzu6q6h">
                <button onClick={() => setShowFilters(s => !s)} aria-label="Filters" className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-1.5" data-id="element-663" data-mp="w2z8rdtpp4jd">
                  <SlidersHorizontalIcon className="w-4 h-4" data-id="element-664" data-mp="2f1ngofa2s35n" />
                </button>
                {showFilters && <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-large z-20 p-2" data-id="element-665" data-mp="2jv8ea67rav4b">
                    {FILTER_DIMENSIONS.map(f => <button key={f} className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg" data-id="element-666" data-mp="2a3vk37yhjsv4">
                        {f}
                      </button>)}
                  </div>}
              </div>
              <PremiumSelect compact value={sort} options={SORTS} onChange={setSort} className="min-w-[9rem]" data-id="element-667" data-mp="2trbidhm653tw" />
              <button aria-label="Export" className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-1.5" data-id="element-668" data-mp="1ibwm6g1safzu">
                <DownloadIcon className="w-4 h-4" data-id="element-669" data-mp="3f3k453z5ugma" />
              </button>
            </div>
          </div>
        </div>

        {/* Lead list */}
        <div className="space-y-2" data-id="element-670" data-mp="369xb3lmc7rgs">
          {filtered.map(({
          l,
          idx
        }) => <LeadRow key={idx} lead={l} onOpen={() => setOpenLeadIdx(idx)} data-id="element-671" data-mp="367uo7agheega" />)}
          {filtered.length === 0 && <div className="text-center py-20 text-slate-400" data-id="element-672" data-mp="2wc12p1ksbamd">
              No leads match these filters.
            </div>}
        </div>
      </motion.div>

      {openLeadIdx !== null && <LeadDetailModal lead={leads[openLeadIdx]} onClose={() => setOpenLeadIdx(null)} onStatusChange={s => updateLead(openLeadIdx, {
      status: s
    })} onOwnerChange={o => updateLead(openLeadIdx, {
      owner: o
    })} data-id="element-673" data-mp="2nbcnw4nkh68f" />}
    </div>;
}
function InlineMetric({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-674" data-mp="2d6mjzt8lrs1k">
      <span className="text-slate-400" data-id="element-675" data-mp="2d7xuebwjzy1n">{icon}</span>
      <div className="text-lg font-display font-bold text-dark-navy" data-id="element-676" data-mp="2h99g46cb3z4k">
        {value}
      </div>
      <span className="text-xs text-slate-500 font-medium" data-id="element-677" data-mp="1kj0hkob42tga">{label}</span>
    </div>;
}
function StageOption({
  label,
  count,
  color,
  active,
  onClick
}: {
  label: string;
  count: number;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-indigo-50 text-primary-indigo' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-678" data-mp="1tli0otvgz5z2">
      <span className="flex items-center gap-2.5" data-id="element-679" data-mp="39wm14c2tjyvr">
        <span className={`w-2 h-2 rounded-full ${color}`} data-id="element-680" data-mp="39zhxhvz0qfi3" />
        <span className="text-sm font-medium" data-id="element-681" data-mp="38havbe4z461c">{label}</span>
      </span>
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-primary-indigo/10 text-primary-indigo' : 'bg-slate-100 text-slate-500'}`} data-id="element-682" data-mp="33hevlr3b70rl">
        {count}
      </span>
    </button>;
}
function LeadRow({
  lead,
  onOpen
}: {
  lead: Lead;
  onOpen: () => void;
}) {
  const bucket = intentBucket(lead.intentScore);
  const hasContact = lead.consent === 'shared';
  return <button onClick={onOpen} className="w-full text-left bg-white border border-slate-200/60 rounded-xl p-5 hover:border-primary-indigo/40 hover:shadow-soft transition-all group" data-id="element-683" data-mp="3mg0xv9akaxx3">
      <div className="flex items-center gap-6" data-id="element-684" data-mp="g7n5wqys7u">
        {/* Identity */}
        <div className="flex items-center gap-3 w-52 flex-shrink-0 min-w-0" data-id="element-685" data-mp="2j4a8senjlj">
          <div className="relative" data-id="element-686" data-mp="e0qvs16o9spb">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" data-id="element-687" data-mp="914jpv2kyii3">
              {lead.name.charAt(0)}
            </div>
            {lead.intentScore >= 90 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center text-[10px]" data-id="element-688" data-mp="xjwsrnu0jwlg">
                🔥
              </span>}
          </div>
          <div className="min-w-0 flex-1" data-id="element-689" data-mp="npo2z7wdahqz">
            <div className="font-semibold text-dark-navy leading-snug truncate" data-id="element-690" data-mp="9w591z658ha5">
              {lead.name}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-691" data-mp="eopvgfmpzs3n">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${bucket.bg} ${bucket.color}`} data-id="element-692" data-mp="3w4509kkuz7j9">
                {bucket.label}
              </span>
              {hasContact && <CheckCircle2Icon className="w-3 h-3 text-green-500" data-id="element-693" data-mp="3n49kt3k9sqdf" />}
            </div>
          </div>
        </div>

        {/* Key info */}
        <div className="hidden lg:flex items-center gap-8 flex-1 min-w-0" data-id="element-694" data-mp="1xljgvyc5yqjq">
          <Fact label="Looking for" value={lead.lookingFor} data-id="element-695" data-mp="1xnw4ar0oxt46" />
          <Fact label="Budget" value={lead.budget} data-id="element-696" data-mp="1sub7316x9361" />
        </div>

        {/* Intent score */}
        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-697" data-mp="1nx1h9kfmepho">
          <div className="text-right" data-id="element-698" data-mp="1juogzbjn7jsa">
            <div className="text-2xl font-display font-bold text-dark-navy leading-none" data-id="element-699" data-mp="1jvppb899xs6b">
              {lead.intentScore}
            </div>
            <div className="text-[10px] text-slate-400 font-medium mt-0.5" data-id="element-700" data-mp="2gib4ul2nhtu4">
              Intent
            </div>
          </div>
          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-701" data-mp="27jhc55sfxnlu">
            <div className={`h-full ${bucket.bar} transition-all`} style={{
            width: `${lead.intentScore}%`
          }} data-id="element-702" data-mp="22l6512tj5r7b" />
          </div>
          <ArrowRightIcon className="w-4 h-4 text-slate-300 group-hover:text-primary-indigo group-hover:translate-x-0.5 transition-all" data-id="element-703" data-mp="r60p2x4e1w5r" />
        </div>
      </div>

      {/* AI summary + received date */}
      <div className="flex items-start gap-4 mt-4" data-id="element-704" data-mp="37rxlm2g1ujy9">
        <div className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed flex-1 min-w-0" data-id="element-705" data-mp="37sp9ss7bhfei">
          <SparklesIcon className="w-4 h-4 text-ai-purple flex-shrink-0 mt-0.5" data-id="element-706" data-mp="3bshkpbk6nxn1" />
          <span className="line-clamp-1" data-id="element-707" data-mp="3glanhtw3cvwh">{lead.summary}</span>
        </div>
        {lead.receivedDate && <span className="flex items-center gap-1 text-xs text-slate-400 font-medium whitespace-nowrap flex-shrink-0 mt-0.5" data-id="element-708" data-mp="3lj2cog5cq3wz">
            <CalendarIcon className="w-3.5 h-3.5" data-id="element-709" data-mp="3qki18i6qmsk4" />
            {lead.receivedDate}
          </span>}
      </div>
    </button>;
}
function Fact({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="min-w-0" data-id="element-710" data-mp="2140hezfpmmd1">
      <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5" data-id="element-711" data-mp="214sbu6xkme1q">
        {label}
      </div>
      <div className="text-sm font-semibold text-dark-navy truncate" data-id="element-712" data-mp="255m0j2iu7wvo">
        {value}
      </div>
    </div>;
}